import React from "react";
import { View, StyleSheet, Text, ScrollView } from "react-native";
import WorkspaceCard, { WorkspaceCardProps } from "../WorkspaceCard/WorkspaceCard";
import { colors, radii, spacing } from "../../../styles/design-system";

export interface WorkspaceSidebarWorkspace extends Omit<WorkspaceCardProps, "onPress" | "onThreadsPress"> {
  id: string;
}

export interface WorkspaceSidebarFooterItem {
  id: string;
  label: string;
}

export interface WorkspaceSidebarProps {
  generalWorkspaces: WorkspaceSidebarWorkspace[];
  courseWorkspaces: WorkspaceSidebarWorkspace[];
  supportWorkspaces: WorkspaceSidebarWorkspace[];
  todoWorkspaces: WorkspaceSidebarWorkspace[];
  settingsWorkspaces: WorkspaceSidebarWorkspace[];
  activeWorkspaceId?: string;
  onWorkspacePress?: (id: string) => void;
  onWorkspaceThreadsPress?: (id: string) => void;
  onFooterItemPress?: (id: string) => void;
}

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  generalWorkspaces,
  courseWorkspaces,
  supportWorkspaces,
  todoWorkspaces,
  settingsWorkspaces,
  activeWorkspaceId,
  onWorkspacePress,
  onWorkspaceThreadsPress,
  onFooterItemPress,
}) => {
  const renderWorkspaceSection = (label: string, workspaces: WorkspaceSidebarWorkspace[]) => {
    if (!workspaces.length) return null;

    return (
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>{label}</Text>
        <View style={styles.sectionCards}>
          {workspaces.map((workspace) => (
            <View key={workspace.id} style={styles.cardWrapper}>
              <WorkspaceCard
                {...workspace}
                isActive={workspace.id === activeWorkspaceId}
                onPress={onWorkspacePress ? () => onWorkspacePress(workspace.id) : undefined}
                onThreadsPress={onWorkspaceThreadsPress ? () => onWorkspaceThreadsPress(workspace.id) : undefined}
              />
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Workspaces</Text>
      </View>

      <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
        {renderWorkspaceSection("GENERAL", generalWorkspaces)}
        {renderWorkspaceSection("COURSES", courseWorkspaces)}
        {renderWorkspaceSection("SUPPORT", supportWorkspaces)}
      </ScrollView>

      <View style={styles.footer}>
        {todoWorkspaces.map((item) => (
          <WorkspaceCard
            key={item.id}
            {...item}
            isActive={item.id === activeWorkspaceId}
            onPress={onWorkspacePress ? () => onWorkspacePress(item.id) : undefined}
          />
        ))}
        {settingsWorkspaces.map((item) => (
          <WorkspaceCard
            key={item.id}
            {...item}
            isActive={item.id === activeWorkspaceId}
            onPress={onWorkspacePress ? () => onWorkspacePress(item.id) : undefined}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 272,
    backgroundColor: colors.grey[25],
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    paddingTop: spacing.lg,
    paddingBottom: spacing.lg,
    paddingHorizontal: spacing.lg,
    justifyContent: "space-between",
  },
  header: {
    marginBottom: spacing.lg,
  },
  headerTitle: {
    fontSize: 12,
    fontWeight: "600",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.grey[500],
  },
  scrollArea: {
    flexGrow: 0,
  },
  scrollContent: {
    paddingBottom: spacing.lg,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: "600",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.grey[400],
    marginBottom: spacing.xs,
  },
  sectionCards: {},
  cardWrapper: {
    marginBottom: spacing.xs,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingTop: spacing.md,
    marginTop: spacing.lg,
  },
});

export default WorkspaceSidebar;
